import { CloseOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import List from '@/components/List/List';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import { SectionTitle } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import {
  notificationQueryKeys,
  useNotificationListQuery,
} from '@/entities/notification';
import WorkflowItem from '@/features/Workflow/WorkflowItem';
import useAppInit from '@/hooks/domain/App/useAppInit';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { useMessage } from '@/messages';
import service from '@/services/index';
import { modalStyle } from '@/styles/style';
import { WORKFLOW_TYPE } from '@/types/workflowConst';

type Props = {
  dashboardAlias: string;
};

const ManageSubscribeModal = (props: Props) => {
  const queryClient = useQueryClient();
  const appInit = useAppInit();
  const wr = useWorkspaceRouter();
  const { closeModal } = useModalContext();
  const m = useMessage();
  const workflowTypes = useMemo(() => [WORKFLOW_TYPE.SUBSCRIPTION], []);
  const { data } = useNotificationListQuery(workflowTypes, {
    enabled: appInit,
  });

  const filteredList = data?.filter(
    (d) =>
      d.type === WORKFLOW_TYPE.SUBSCRIPTION &&
      d.subscription?.target === props.dashboardAlias,
  );

  const onToggleHandler = async (alias: string, status: boolean) => {
    await service.workflow.toggleWorkflow(alias, status);
    queryClient.setQueryData(
      notificationQueryKeys.list(workflowTypes),
      (old: any) =>
        old?.map((workflow: Workflow) =>
          workflow.alias === alias
            ? {
                ...workflow,
                enable: status,
              }
            : workflow,
        ),
    );
  };

  const onSelectHandler = (workflow: Workflow) => {
    wr.push(`/workflow/${workflow.alias}`);
  };

  return (
    <Style g={32}>
      <VFlex g={8}>
        <Flex j="space-between" a="center">
          <Heading type="h3">Subscribe dashboard</Heading>
          <IconButton
            sizeType="m"
            outlined={false}
            icon={<CloseOutlined />}
            onClick={closeModal}
          />
        </Flex>
        <Typo type="body2">{m.workflow('WORKFLOW_DESCRIPTION')}</Typo>
      </VFlex>
      <VFlex g={20}>
        <SectionTitle className="section-title">Subscription list</SectionTitle>
        <List
          className="gap-4 h-100"
          data={filteredList}
          render={(w) => (
            <WorkflowItem
              {...(w as Workflow)}
              onToggle={onToggleHandler}
              onClick={onSelectHandler}
            />
          )}
        />
      </VFlex>
    </Style>
  );
};

const Style = styled(VFlex)`
  padding: 32px;
  ${modalStyle};
  .section-title {
    color: ${(props) => props.theme.colors.gray600};
  }
`;

export default ManageSubscribeModal;
